import { api } from '@common-sense-privacy/common'

import commonSenseApi from '@/state/apis/commonSense'

const productEvaluationService = commonSenseApi.injectEndpoints({
  endpoints: build => ({
    dismissEvaluation: build.mutation<
      api.endpoints.organization.product.evaluation.types.DismissResponseBody,
      api.endpoints.organization.product.evaluation.types.DismissRequest
    >({
      query: ({
        params: {
          id,
          productId,
          productEvaluationId,
        },
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/evaluations/${productEvaluationId}/dismiss`,
        method: 'POST',
      }),
      // The card renders off the `latest` cache, so it is patched here rather than left
      // to the invalidation refetch: the X should close the card on the click, not a round
      // trip later. Undone if the request fails, so a failed dismissal reappears rather
      // than being silently lost until the next reload.
      async onQueryStarted({
        params: {
          id, productId, productEvaluationId,
        },
      }, {
        dispatch, queryFulfilled,
      }) {
        const patch = dispatch(
          productEvaluationService.util.updateQueryData(
            'getLatestEvaluation',
            {
              params: {
                id,
                productId,
              },
            },
            draft => {
              if (draft.data?.id === productEvaluationId && !draft.data.dismissedAt) {
                // The contract types this as a Date, but the wire (and so the cache) carries
                // an ISO string - and the store's serializable check would reject a Date.
                draft.data.dismissedAt = new Date().toISOString() as unknown as Date
              }
            },
          ),
        )

        try {
          await queryFulfilled
        }
        catch {
          patch.undo()
        }
      },
      invalidatesTags: [ 'ProductEvaluation' ],
    }),
    getEvaluation: build.query<
      api.endpoints.organization.product.evaluation.types.GetResponseBody,
      api.endpoints.organization.product.evaluation.types.GetRequest
    >({
      query: ({
        params: {
          id,
          productId,
          productEvaluationId,
        },
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/evaluations/${productEvaluationId}`,
        method: 'GET',
      }),
      // `currentAnswer` on each mapped answer is read from the product's privacy-issue
      // answers, so any PUT that writes one invalidates this - otherwise a return to the
      // screen within the cache window shows the answers as they were when first fetched.
      providesTags: [
        'ProductEvaluation',
        'ProductEvaluationAnswers',
      ],
    }),
    getLatestEvaluation: build.query<
      api.endpoints.organization.product.evaluation.types.GetLatestResponseBody,
      api.endpoints.organization.product.evaluation.types.GetLatestRequest
    >({
      query: ({
        params: {
          id, productId,
        },
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/evaluations/latest`,
        method: 'GET',
      }),
      providesTags: [ 'ProductEvaluation' ],
    }),
    postEvaluation: build.mutation<
      api.endpoints.organization.product.evaluation.types.PostResponseBody,
      api.endpoints.organization.product.evaluation.types.PostRequest
    >({
      query: ({
        params: {
          id, productId,
        },
      }) => ({
        url: `v1/organizations/${id}/products/${productId}/evaluations`,
        method: 'POST',
      }),
      invalidatesTags: [ 'ProductEvaluation' ],
    }),
  }),
})

export default productEvaluationService

export const {
  useDismissEvaluationMutation,
  useGetEvaluationQuery,
  useGetLatestEvaluationQuery,
  useLazyGetLatestEvaluationQuery,
  usePostEvaluationMutation,
} = productEvaluationService

export const {
  endpoints: {
    dismissEvaluation,
    getEvaluation,
    getLatestEvaluation,
    postEvaluation,
  },
} = productEvaluationService
